import React from 'react';
import { Trash2, CheckCircle, XCircle, Database, Workflow } from 'lucide-react';

/**
 * Exibe progresso de exclusão de usuário: workflows, tabelas, schemas
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - result: objeto retornado de DELETE /api/users/:id
 */
const DeleteProgressModal = ({ isOpen, onClose, result }) => {
  if (!isOpen) return null;

  const dataDeleted = !!result?.dataDeleted;
  const tablesDeleted = Array.isArray(result?.tablesDeleted) ? result.tablesDeleted : [];
  const schemaDeleted = !!result?.schemaDeleted;
  const workflowsDeleted = typeof result?.workflowsDeleted === 'number' ? result.workflowsDeleted : 0;
  const errors = Array.isArray(result?.errors) ? result.errors : [];
  const workflowErrors = Array.isArray(result?.workflowErrors) ? result.workflowErrors : [];

  const renderStatus = (ok) => ok
    ? <CheckCircle className="w-5 h-5 text-green-600" />
    : <XCircle className="w-5 h-5 text-red-600" />;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full shadow-2xl border border-gray-200/50 dark:border-gray-700/50 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Exclusão de Usuário</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white">Fechar</button>
        </div>

        {/* Dados (MariaDB/Supabase) */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Dados (Schemas/Tabelas)</span>
          </div>
          <div className="flex items-start gap-3 py-2">
            {renderStatus(dataDeleted)}
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-white">Exclusão de dados</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{dataDeleted ? 'Concluída' : 'Parcial ou não realizada'}</div>
            </div>
          </div>
          <div className="flex items-start gap-3 py-2">
            {renderStatus(tablesDeleted.length > 0)}
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-white">Tabelas</div>
              <pre className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words mt-1 max-h-36 overflow-auto">{JSON.stringify(tablesDeleted, null, 2)}</pre>
            </div>
          </div>
          <div className="flex items-start gap-3 py-2">
            {renderStatus(schemaDeleted)}
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-white">Schema</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{schemaDeleted ? 'Removido' : 'Não removido'}</div>
            </div>
          </div>
          {errors.length > 0 && (
            <div className="mt-2 text-sm text-red-600">Erros: {errors.join('; ')}</div>
          )}
        </div>

        {/* Workflows */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Workflow className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Workflows (n8n)</span>
          </div>
          <div className="flex items-start gap-3 py-2">
            {renderStatus(workflowsDeleted > 0)}
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-white">Deletados</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Total: {workflowsDeleted}</div>
            </div>
          </div>
          {workflowErrors.length > 0 && (
            <pre className="text-xs text-red-600 whitespace-pre-wrap break-words mt-1 max-h-36 overflow-auto">{JSON.stringify(workflowErrors, null, 2)}</pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteProgressModal;